import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Menu } from '@mantine/core';
import {
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cva } from 'class-variance-authority';
import { ArrowRight, Star } from 'lucide-react';

import { OuterClonotypeTableColumns } from './OuterClonotypeTableColumns';
import { AppRoutes, makeExperimentRoute } from '../../../appRoutes';
import resetFilterIcon from '../../../assets/reset-filter.svg';
import { MultiAlignmentsModal } from '../../../clonotype-network-map/MultiAlignmentsModal';
import type { NetworkMapClonotype } from '../../../clonotype-network-map/networkMap.utils';
import { useSendToExportSummary } from '../../../export-summary/hooks/useSendToExportSummary';
import {
  clearSelection,
  selectCollapsedClonotypes,
  selectHideSingletons,
  selectSearchQuery,
  selectSelectedSequences,
  setClonotypeCollapsedFlag,
  setSearchQuery,
  setSelectedSequences,
  toggleHighlightedRegion,
  toggleSequence,
} from '../../../store/slices/antibodyAnalysis.slice';
import type { AppDispatch } from '../../../store/store';
import { useGetExperiment } from '../../../store/store';
import { useChainWidths } from '../../context/ChainWidthContext';
import { useClonotypeSelection } from '../../hooks/useClonotypeSelection';
import { useVisibleRows } from '../../hooks/useVisibleRows';
import type { AntibodyResponse } from '../../types';
import { deriveClonotypeRow } from '../../utilities/clonotypeUtils';
import { frozenCellStyle } from '../../utilities/frozenColumn';
import { selectTopAPSSequences } from '../../utilities/selectionUtils';
import { InnerChildSequenceTable } from '../InnerSequenceTable/InnerChildSequenceTable';
import { getInnerTableWidth } from '../InnerSequenceTable/InnerChildSequenceTableColumns';
import { ColumnVisibilityMenu } from '../common/ColumnVisibilityMenu';
import { SearchPill } from '../common/SearchPill';

const s = {
  container: 'flex flex-col h-full',
  toolbar: 'flex items-center gap-2 px-4 py-3 border-b border-panel-border bg-white',
  toolbarLeft: 'flex items-center gap-3 text-xs text-muted-foreground flex-1 -ml-2',
  toolbarActions: 'flex items-center gap-2',
  resetIcon: 'w-3.5 h-3.5',
  actionIcon: 'w-3 h-3',
  exportIcon: 'w-4 h-4',
  tableArea: 'relative min-h-0 mt-2',
  tableWrapper: 'h-full overflow-auto bg-white pb-20',
  table: 'w-full border-collapse table-fixed',
  thead: 'sticky top-0 bg-white z-20 border-b border-border',
  headerCell: 'text-left text-sm font-medium text-[#3d3d3d] border-r border-border px-3 py-3 align-top',
  bodyCell: 'border-r border-border text-sm text-[#3d3d3d] px-3 py-3 align-middle',
  innerCell: 'p-0',
  emptyState: 'text-center py-8 text-sm text-muted-foreground',
  spacerRow: 'h-3 bg-white',
  floatingBar: 'absolute bottom-4 right-4 z-30',
  exportBtn:
    'px-6 py-3 text-sm font-medium text-white bg-[#0064b0] rounded-[44px] hover:bg-[#0053a0] inline-flex items-center gap-2.5 transition-colors border-0 cursor-pointer shadow-lg',
} as const;

const clonotypeRowStyle = cva('cursor-pointer transition-colors', {
  variants: {
    expanded: { true: '', false: 'border-b border-border' },
    even: { true: 'bg-[#f7fbff]', false: 'bg-white' },
  },
  compoundVariants: [{ expanded: false, class: 'hover:bg-gray-100' }],
  defaultVariants: { expanded: false, even: false },
});

const Z_INDEX_FROZEN_BODY = 16;
const Z_INDEX_FROZEN_HEADER = 1;
const ROW_BG = { even: '#f7fbff', odd: '#ffffff' } as const;
const ROWSPAN_COLUMN_IDS = ['select', 'networkMap'];
const FROZEN_COLUMN_IDS = [...ROWSPAN_COLUMN_IDS, 'clonotypeId'];

interface OuterParentClonotypeTableProps {
  clonotypes: AntibodyResponse[];
  chainType: 'all' | 'heavy' | 'light';
  clonotypeMethod: string;
  networkMapByClonotypeId?: Map<number, NetworkMapClonotype>;
}

export function OuterParentClonotypeTable({ clonotypes, chainType, clonotypeMethod, networkMapByClonotypeId }: OuterParentClonotypeTableProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { id: experimentId } = useParams<{ id: string }>();
  const { data: experiment } = useGetExperiment({ id: experimentId! });
  const { sendToExportSummary, isSending } = useSendToExportSummary(experimentId);
  const navigate = useNavigate();
  const searchQuery = useSelector(selectSearchQuery);
  const selectedSequencesArray = useSelector(selectSelectedSequences);
  const collapsed = useSelector(selectCollapsedClonotypes);
  const hideSingletons = useSelector(selectHideSingletons);
  const selectedSequences = React.useMemo(() => new Set(selectedSequencesArray), [selectedSequencesArray]);
  const exportableSequenceCount = React.useMemo(
    () => clonotypes.reduce((n, c) => n + c.sequences.filter((seq) => selectedSequences.has(`${c.id}-${seq.metadata.sequenceId}`)).length, 0),
    [clonotypes, selectedSequences],
  );
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'clonotypeSize', desc: true }]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [sequenceFilter, setSequenceFilter] = React.useState<'all' | 'unselected'>('all');
  const [activeExpand, setActiveExpand] = React.useState<'all' | 'selected' | 'collapsed' | null>(null);
  const [tanglegramClonotype, setTanglegramClonotype] = React.useState<NetworkMapClonotype | null>(null);

  // Sort by clonotypeId as the stable base order.Otherwise a clonotype unchecked and re-checked in
  // the map (its data re-fetched in a different order) would reappear in a different table row.
  const tableData = React.useMemo(() => clonotypes.map(deriveClonotypeRow).sort((a, b) => a.clonotypeId - b.clonotypeId), [clonotypes]);
  const selection = useClonotypeSelection({ tableData, selectedSequences, collapsed });
  // effectiveFilter drives what's actually shown — activeExpand='selected' overrides the
  // sequenceFilter button without changing its label/highlight state.
  const effectiveFilter = activeExpand === 'selected' ? 'selected' : activeExpand === 'all' ? 'all' : sequenceFilter;

  const visibleRows = useVisibleRows({
    tableData,
    hideSingletons,
    searchQuery,
    sequenceFilter: effectiveFilter,
    selectedSequences,
  });

  const columnValues = React.useMemo(
    () => ({
      igIsotype: tableData.map((r) => r.igIsotype ?? ''),
      vjGeneHeavy: tableData.map((r) => r.vjGeneHeavy),
      vjGeneLight: tableData.map((r) => r.vjGeneLight),
      representativeCDRH3: tableData.map((r) => r.representativeCDRH3),
      representativeCDRL3: tableData.map((r) => r.representativeCDRL3),
    }),
    [tableData],
  );

  const handleSequenceToggle = React.useCallback((cId: number, sId: string) => dispatch(toggleSequence(`${cId}-${sId}`)), [dispatch]);

  // The outer-row checkbox is EXPORT selection for the whole clonotype (all its sequences), NOT
  // network-map selection. The network map is the sole source of truth for what's in the table:
  // only deselecting a clonotype ON THE MAP removes its row. Toggling this checkbox just
  // checks/unchecks all the clonotype's sequences for export; the clonotype stays selected on the
  // map and its row stays in the table (so an unchecked sequence can be re-selected later).
  type OuterRow = { clonotypeId: number; sequences: { metadata: { sequenceId: string } }[] };
  const seqKeysOf = React.useCallback((row: OuterRow) => row.sequences.map((seq) => `${row.clonotypeId}-${seq.metadata.sequenceId}`), []);

  // Count how many of the clonotype's sequences are selected in one pass → both checkbox states:
  // checked = all selected, indeterminate = some (but not all) selected.
  const clonotypeSelectionState = React.useCallback(
    (row: OuterRow) => {
      const keys = seqKeysOf(row);
      const selectedCount = keys.reduce((n, k) => n + (selectedSequences.has(k) ? 1 : 0), 0);
      return {
        checked: keys.length > 0 && selectedCount === keys.length,
        indeterminate: selectedCount > 0 && selectedCount < keys.length,
      };
    },
    [selectedSequences, seqKeysOf],
  );

  const handleToggleClonotypeChecked = React.useCallback(
    (row: OuterRow) => {
      const rowId = `c_${row.clonotypeId}`;
      const seqKeys = seqKeysOf(row);
      const allSelected = seqKeys.length > 0 && seqKeys.every((k) => selectedSequences.has(k));
      if (allSelected) {
        dispatch(setSelectedSequences(selectedSequencesArray.filter((k) => !seqKeys.includes(k))));
        dispatch(setClonotypeCollapsedFlag({ rowId, collapsed: true }));
      } else {
        dispatch(setSelectedSequences([...new Set([...selectedSequencesArray, ...seqKeys])]));
        dispatch(setClonotypeCollapsedFlag({ rowId, collapsed: false }));
      }
    },
    [dispatch, selectedSequences, selectedSequencesArray, seqKeysOf],
  );

  const handleRegionClick = React.useCallback(
    (r: string | null) => {
      if (r) {
        dispatch(toggleHighlightedRegion(r));
      }
    },
    [dispatch],
  );
  // SSTSW-1045: Export sends the selection to Export summary as a new timestamped tab, and
  // takes the user there. The download now happens, at the
  // end of the workflow, from Finalize export on the Selected tab (SSTSW-1047).
  const selectedNodeIds = React.useMemo(() => {
    const ids = new Set<number>();
    for (const clonotype of clonotypes) {
      const mapNodes = networkMapByClonotypeId?.get(clonotype.id)?.nodes;
      if (!mapNodes) {
        continue;
      }
      for (const sequence of clonotype.sequences) {
        if (!selectedSequences.has(`${clonotype.id}-${sequence.metadata.sequenceId}`)) {
          continue;
        }
        const barcodes = new Set(sequence.metadata.originalBarcodes);
        const match = mapNodes.find((node) => node.cells.some((cell) => barcodes.has(cell.barcode)));
        if (match) {
          ids.add(parseInt(match.id, 10));
        }
      }
    }
    return [...ids];
  }, [clonotypes, selectedSequences, networkMapByClonotypeId]);
  const canResolveAllSelected = selectedNodeIds.length === exportableSequenceCount;

  const handleExport = React.useCallback(async () => {
    const created = await sendToExportSummary(selectedNodeIds);
    if (created && experimentId) {
      navigate(makeExperimentRoute(experimentId, AppRoutes.ExperimentExportSummary), { state: { newExportId: created.exportId } });
    }
  }, [sendToExportSummary, selectedNodeIds, navigate, experimentId]);
  const handleSelectTopAPS = React.useCallback((n: number) => dispatch(setSelectedSequences(selectTopAPSSequences(clonotypes, n))), [clonotypes, dispatch]);
  const handleClearAll = React.useCallback(() => {
    setColumnFilters([]);
    setSequenceFilter('all');
    setActiveExpand(null);
    dispatch(clearSelection());
    dispatch(setSearchQuery(''));
  }, [dispatch]);

  const columns = React.useMemo(
    () => OuterClonotypeTableColumns({ ...selection, clonotypeSelectionState, handleToggleClonotypeChecked, columnValues, searchQuery }),
    [selection, clonotypeSelectionState, handleToggleClonotypeChecked, columnValues, searchQuery],
  );
  const tableMeta = React.useMemo(
    () => ({ networkMapByClonotypeId, onTanglegramClick: setTanglegramClonotype }),
    [networkMapByClonotypeId, setTanglegramClonotype],
  );
  const table = useReactTable({
    data: visibleRows,
    columns,
    state: { sorting, columnFilters, columnVisibility },
    meta: tableMeta,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => `c_${row.clonotypeId}`,
  });

  // ── Row virtualization ──
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const theadRef = React.useRef<HTMLTableSectionElement>(null);
  React.useLayoutEffect(() => {
    const thead = theadRef.current;
    const scroller = scrollRef.current;
    if (!thead || !scroller) {
      return undefined;
    }
    const sync = () => scroller.style.setProperty('--outer-header-h', `${thead.offsetHeight}px`);
    const ro = new ResizeObserver(sync);
    ro.observe(thead);
    sync();
    return () => ro.disconnect();
  }, []);
  const { rows } = table.getRowModel();
  const colCount = table.getVisibleLeafColumns().length;
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    // Collapsed-row estimate; real heights (expanded rows are much taller) are measured on mount.
    estimateSize: () => 56,
    overscan: 6,
    // Each virtual item is a whole clonotype (its own <tbody>: parent row + inner sequence table);
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const leafCols = table.getVisibleLeafColumns();
  const rowspanColCount = leafCols.filter((c) => ROWSPAN_COLUMN_IDS.includes(c.id)).length;
  const frozenColCount = leafCols.filter((c) => FROZEN_COLUMN_IDS.includes(c.id)).length;
  const pinnedLeftWidth = leafCols.slice(0, rowspanColCount).reduce((sum, c) => sum + c.getSize(), 0);
  const pinnedLeftOffset = (idx: number) => leafCols.slice(0, idx).reduce((sum, c) => sum + c.getSize(), 0);
  const { heavy: heavyChainWidth, light: lightChainWidth } = useChainWidths();
  const innerTableWidth = getInnerTableWidth(heavyChainWidth, lightChainWidth, chainType);
  const outerColumnsWidth = table.getTotalSize();
  const contentWidth = pinnedLeftWidth + innerTableWidth;
  const tableMinWidth = Math.max(outerColumnsWidth, contentWidth);
  const spacerColWidth = Math.max(0, contentWidth - outerColumnsWidth);

  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  return (
    <div className={s.container}>
      <div className={s.toolbar}>
        <div className={s.toolbarLeft}>
          <Button
            variant="subtle"
            size="compact-xs"
            td="underline"
            fw={activeExpand === 'all' ? 700 : 400}
            c={activeExpand === 'all' ? '#0064b0' : '#3d3d3d'}
            onClick={() => {
              selection.handleExpandAll();
              setSequenceFilter('all');
              setActiveExpand('all');
            }}
          >
            Expand all
          </Button>
          <Button
            variant="subtle"
            size="compact-xs"
            td="underline"
            fw={activeExpand === 'selected' ? 700 : 400}
            c={activeExpand === 'selected' ? '#0064b0' : '#3d3d3d'}
            onClick={() => {
              selection.handleExpandSelected();
              setSequenceFilter('all');
              setActiveExpand('selected');
            }}
          >
            Expand selected
          </Button>
          <Button
            variant="subtle"
            size="compact-xs"
            td="underline"
            fw={activeExpand === 'collapsed' ? 700 : 400}
            c={activeExpand === 'collapsed' ? '#0064b0' : '#3d3d3d'}
            onClick={() => {
              selection.handleCollapseAll();
              setSequenceFilter('all');
              setActiveExpand('collapsed');
            }}
          >
            Collapse all
          </Button>
          <Button
            variant="subtle"
            size="compact-xs"
            td="underline"
            fw={sequenceFilter !== 'all' ? 700 : 400}
            c={sequenceFilter !== 'all' ? '#0064b0' : '#3d3d3d'}
            onClick={() => {
              const turningOn = sequenceFilter !== 'unselected';
              setActiveExpand(null);
              setSequenceFilter(turningOn ? 'unselected' : 'all');
              if (turningOn) {
                selection.handleExpandAll();
              }
            }}
          >
            Show unselected
          </Button>
        </div>
        <SearchPill value={searchQuery} onChange={(value) => dispatch(setSearchQuery(value))} placeholder="Search clonotypes" />
        <div className={s.toolbarActions}>
          <Button
            variant="subtle"
            size="compact-xs"
            c="#3d3d3d"
            styles={{ section: { marginInlineEnd: 4 } }}
            leftSection={<img src={resetFilterIcon} alt="" className={s.resetIcon} />}
            onClick={handleClearAll}
          >
            Reset
          </Button>
          <ColumnVisibilityMenu table={table} />
        </div>
        <Menu>
          <Menu.Target>
            <Button variant="subtle" size="compact-xs" c="#3d3d3d" styles={{ section: { marginInlineEnd: 4 } }} leftSection={<Star className={s.actionIcon} />}>
              Select top APS
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Select top X sequences per clonotype</Menu.Label>
            {[1, 3, 5, 10].map((n) => (
              <Menu.Item key={n} onClick={() => handleSelectTopAPS(n)}>
                Top {n} APS
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </div>

      <div className={s.tableArea}>
        <div className={s.tableWrapper} ref={scrollRef} style={{ '--pinned-left-w': `${pinnedLeftWidth}px` } as React.CSSProperties}>
          <table className={s.table} style={{ minWidth: tableMinWidth }}>
            {spacerColWidth > 0 && (
              <colgroup>
                {leafCols.map((c) => (
                  <col key={c.id} style={{ width: c.getSize() }} />
                ))}
                <col style={{ width: spacerColWidth }} />
              </colgroup>
            )}
            <thead className={s.thead} ref={theadRef}>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h, hIdx) => {
                    const isPinned = hIdx < frozenColCount;
                    return (
                      <th
                        key={h.id}
                        className={s.headerCell}
                        style={{
                          width: h.getSize(),
                          ...(isPinned ? frozenCellStyle(pinnedLeftOffset(hIdx), hIdx === frozenColCount - 1, Z_INDEX_FROZEN_HEADER, ROW_BG.odd) : {}),
                        }}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    );
                  })}
                  {/* Opaque cell over the spacer column so the sticky header covers the wide inner
                    sequence table scrolling underneath). */}
                  {spacerColWidth > 0 && <th aria-hidden className={s.headerCell} style={{ background: ROW_BG.odd }} />}
                </tr>
              ))}
            </thead>
            {/* Top spacer — reserves the height of the virtualized rows scrolled off the top. */}
            {paddingTop > 0 && (
              <tbody aria-hidden>
                <tr>
                  <td colSpan={colCount} style={{ height: paddingTop, padding: 0, border: 0 }} />
                </tr>
              </tbody>
            )}
            {virtualItems.map((virtualRow) => {
              const row = rows[virtualRow.index];
              const rowId = `c_${row.original.clonotypeId}`;
              const isExpanded = !collapsed[rowId];
              const isEven = virtualRow.index % 2 === 1;
              const visibleCells = row.getVisibleCells();
              // One <tbody> per clonotype so the virtualizer can measure the whole unit
              // (parent row + inner sequence table) as a single item via data-index.
              return (
                <tbody key={row.id} data-index={virtualRow.index} ref={rowVirtualizer.measureElement}>
                  <tr className={clonotypeRowStyle({ expanded: isExpanded, even: isEven })} onClick={() => selection.toggleExpanded(rowId)}>
                    {visibleCells.map((cell, cellIdx) => {
                      // rowSpan applies only to checkbox + network map (they span the inner table);
                      // the horizontal freeze extends one column further to include Clonotype ID.
                      const isRowspanPinned = cellIdx < rowspanColCount;
                      const isStickyPinned = cellIdx < frozenColCount;
                      // Frozen-left columns keep the clonotype identity put on horizontal scroll
                      return (
                        <td
                          key={cell.id}
                          className={s.bodyCell}
                          style={{
                            width: cell.column.getSize(),
                            verticalAlign: isRowspanPinned ? 'top' : 'middle',
                            ...(isStickyPinned
                              ? frozenCellStyle(
                                  pinnedLeftOffset(cellIdx),
                                  cellIdx === frozenColCount - 1,
                                  Z_INDEX_FROZEN_BODY,
                                  isEven ? ROW_BG.even : ROW_BG.odd,
                                )
                              : {}),
                          }}
                          rowSpan={isExpanded && isRowspanPinned ? 2 : undefined}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                  {isExpanded && (
                    <>
                      <tr style={{ background: isEven ? ROW_BG.even : ROW_BG.odd }}>
                        <td colSpan={visibleCells.length - rowspanColCount} className={s.innerCell}>
                          <InnerChildSequenceTable
                            clonotypeId={row.original.clonotypeId}
                            sequences={
                              effectiveFilter === 'unselected'
                                ? row.original.sequences.filter((seq) => !selectedSequences.has(`${row.original.clonotypeId}-${seq.metadata.sequenceId}`))
                                : effectiveFilter === 'selected'
                                  ? row.original.sequences.filter((seq) => selectedSequences.has(`${row.original.clonotypeId}-${seq.metadata.sequenceId}`))
                                  : row.original.sequences
                            }
                            isSequenceSelected={selection.isSequenceSelected}
                            onSequenceToggle={handleSequenceToggle}
                            onRegionClick={handleRegionClick}
                            chainType={chainType}
                            heavyRegions={row.original.heavyRegions}
                            lightRegions={row.original.lightRegions}
                          />
                        </td>
                      </tr>
                      <tr className={s.spacerRow} />
                    </>
                  )}
                </tbody>
              );
            })}
            {/* Bottom spacer — reserves the height of the virtualized rows scrolled off the bottom. */}
            {paddingBottom > 0 && (
              <tbody aria-hidden>
                <tr>
                  <td colSpan={colCount} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                </tr>
              </tbody>
            )}
            {rows.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={colCount} className={s.emptyState}>
                    No clonotypes match the current filters
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>

        {exportableSequenceCount > 0 && (
          <div className={s.floatingBar}>
            <button
              type="button"
              className={s.exportBtn}
              onClick={handleExport}
              disabled={isSending || !canResolveAllSelected}
              title={canResolveAllSelected ? undefined : 'Still loading node data — try again in a moment'}
            >
              Export {exportableSequenceCount} sequence{exportableSequenceCount !== 1 ? 's' : ''}
              <ArrowRight className={s.exportIcon} />
            </button>
          </div>
        )}
      </div>
      {tanglegramClonotype && (
        <MultiAlignmentsModal
          onClose={() => setTanglegramClonotype(null)}
          clonotypeId={tanglegramClonotype.clonotypeId}
          barcodes={tanglegramClonotype.nodes.flatMap((node) => node.cells.map((cell) => cell.barcode))}
        />
      )}
    </div>
  );
}
